import React from "react";
import { css, keyframes } from "@emotion/css";

/**
 * Shared loading indicator for in-flight submissions.
 *
 * Used across all interaction kinds (single_select, free_text, code,
 * ordering, sequence_gap_select) so the learner sees the same visual
 * signal everywhere when their answer is being processed by the server.
 *
 * Two sizes:
 * - ``inline`` (default) — small 14px spinner for placement next to text
 *   or inside buttons.
 * - ``block`` — 18px spinner with a status label, used when replacing
 *   an interaction's buttons with a "processing" panel.
 */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const spinnerBase = css`
  display: inline-block;
  border-radius: 50%;
  border-style: solid;
  border-color: var(--color-border);
  border-top-color: var(--color-primary);
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const spinnerInline = css`
  width: 14px;
  height: 14px;
  border-width: 2px;
`;

const spinnerBlock = css`
  width: 18px;
  height: 18px;
  border-width: 2.5px;
`;

const inlineContainer = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-secondary);
  font-size: 0.9em;
`;

const blockContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px;
  color: var(--color-text-secondary);
  font-size: 0.92em;
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: 10px;
`;

export interface PendingIndicatorProps {
  /** Optional status text next to the spinner (e.g. "Sending your answer…"). */
  label?: string;
  /** ``inline`` sits next to text; ``block`` is a full-width panel. */
  variant?: "inline" | "block";
  /** Optional test id for E2E / unit tests. */
  "data-testid"?: string;
}

export const PendingIndicator: React.FC<PendingIndicatorProps> = ({
  label,
  variant = "inline",
  "data-testid": testId = "pending-indicator",
}) => {
  const containerClass = variant === "block" ? blockContainer : inlineContainer;
  const spinnerClass = variant === "block" ? spinnerBlock : spinnerInline;

  return (
    <div className={containerClass} data-testid={testId} role="status" aria-live="polite">
      <span className={`${spinnerBase} ${spinnerClass}`} aria-hidden="true" />
      {label && <span>{label}</span>}
    </div>
  );
};
